import { useNavigate } from "react-router-dom";
import { AlertTriangle, ChevronRight } from "lucide-react";
import { cn } from "@/lib/cn";
import type { SupervisorKpiCard } from "@/api/types";

/**
 * S2 Block 1 — Team KPI row (8 cards, mockup v2). Counts are backend-owned; the
 * card click routes to a filtered drilldown with the SAME team semantics (C2 /
 * AC-SH-KPI-004). «Менеджери команди» is informational (no drill). States:
 * zero→neutral (006) · error→error-state (007) · data-unavailable→never fake-zero
 * (AC-SH-AR) · disabled→card hidden (hide-if-IR-off).
 */

// FE owns the exact route+filter (page param names) — backend clickTarget is advisory.
const ROUTE: Record<string, string | null> = {
  managers: null, // informational
  openTasks: "/tasks?scope=team",
  overdueTasks: "/tasks?scope=team&filter=overdue",
  meetingsNoReport: "/meetings?scope=team&filter=report_required",
  profilesToReview: "/needs-review?scope=team",
  requestsSlaBreach: "/requests?filter=sla",
  clientsNoNextAction: "/clients?scope=team&dataQuality=no_next_action",
  arRisk: "/clients?scope=team&debtStatus=has_overdue",
};

const LABEL: Record<string, string> = {
  managers: "Менеджери команди",
  openTasks: "Відкриті задачі",
  overdueTasks: "Прострочені задачі",
  meetingsNoReport: "Зустрічі без звіту",
  profilesToReview: "Профілі на перевірку",
  requestsSlaBreach: "Запити-SLA breach",
  clientsNoNextAction: "Клієнти без next-action",
  arRisk: "Боржники / AR-risk",
};

const SUB: Record<string, string> = {
  managers: "активні · контекст",
  openTasks: "по команді",
  overdueTasks: "потребує дії",
  meetingsNoReport: "overdue",
  profilesToReview: "pending",
  requestsSlaBreach: "прострочено",
  clientsNoNextAction: "потребує дії",
  arRisk: "джерело: BI/AR",
};

const ALERT = new Set(["overdueTasks", "clientsNoNextAction"]); // red
const WARN = new Set(["meetingsNoReport", "requestsSlaBreach", "arRisk"]); // amber

export function SupervisorKpiGrid({ kpis }: { kpis: SupervisorKpiCard[] }) {
  const navigate = useNavigate();
  // disabled cards (e.g. requests when IR off) are hidden — no dead card (§0).
  const visible = kpis.filter((k) => k.sourceStatus !== "disabled");

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4">
      {visible.map((k) => (
        <KpiCell key={k.key} card={k} onOpen={(route) => navigate(route)} />
      ))}
    </div>
  );
}

function KpiCell({
  card,
  onOpen,
}: {
  card: SupervisorKpiCard;
  onOpen: (route: string) => void;
}) {
  const label = LABEL[card.key] ?? card.key;
  const route = ROUTE[card.key] ?? null;
  const isError = card.sourceStatus === "error";
  const isUnavailable = card.sourceStatus === "data-unavailable";
  const positive = typeof card.value === "number" && card.value > 0;
  const tone = isError || isUnavailable ? "muted" : positive && ALERT.has(card.key) ? "alert" : positive && WARN.has(card.key) ? "warn" : "neutral";
  const clickable = route !== null && !isError && !isUnavailable;

  const body = (
    <>
      <div className="flex items-center justify-between text-xs font-medium text-muted-foreground">
        <span>{label}</span>
        {clickable && (
          <ChevronRight className="h-3.5 w-3.5 opacity-40 transition-opacity group-hover:opacity-100" />
        )}
      </div>
      {isError ? (
        <>
          <div className="mt-2.5 flex items-center gap-1.5 text-sm font-semibold text-[hsl(var(--destructive))]">
            <AlertTriangle className="h-4 w-4" />
            Помилка завантаження
          </div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">спробуйте оновити</div>
        </>
      ) : isUnavailable ? (
        <>
          <div className="mt-2.5 text-sm font-semibold text-muted-foreground">Дані недоступні</div>
          <div className="mt-0.5 text-[11px] text-muted-foreground">BI/AR офлайн — не «0»</div>
        </>
      ) : (
        <>
          <div
            className={cn(
              "mt-1.5 text-3xl font-semibold tabular-nums tracking-tight",
              tone === "alert" && "text-[hsl(var(--destructive))]",
              tone === "warn" && "text-[hsl(var(--warning-foreground))]",
              tone === "neutral" && card.value === 0 && "text-muted-foreground",
            )}
          >
            {card.value ?? 0}
          </div>
          <div
            className={cn(
              "mt-0.5 text-[11px]",
              tone === "alert" ? "text-[hsl(var(--destructive))]" : tone === "warn" ? "text-[hsl(var(--warning-foreground))]" : "text-muted-foreground",
            )}
          >
            {card.value === 0 ? "нейтральний стан" : SUB[card.key] ?? ""}
          </div>
        </>
      )}
    </>
  );

  const base = cn(
    "group relative rounded-xl border p-3.5 text-left transition-all",
    tone === "alert" && "border-[hsl(var(--destructive)/0.25)] bg-[hsl(var(--destructive)/0.06)]",
    tone === "warn" && "border-[hsl(var(--warning-foreground)/0.25)] bg-[hsl(var(--warning)/0.5)]",
    (tone === "neutral" || tone === "muted") && "border-border bg-card",
    isError && "border-dashed",
  );

  if (!clickable) {
    return (
      <div className={base} aria-label={label}>
        {body}
      </div>
    );
  }
  return (
    <button type="button" onClick={() => onOpen(route as string)} className={cn(base, "hover:border-primary/50 hover:shadow-sm")}>
      {body}
    </button>
  );
}
